const useThemeUtils = ({ colors, fonts }) => {
    const customBorders = (color, size = 1) => `${size}px solid ${getColor(color)}`;
    const borderColor = (color) => `1px solid ${getColor(color)}`;
    const borderColorDouble = (color) => `1px solid ${getColor(color)}`;
    const lightenDarkenColor = function (color, amt) {
        let usePound = false;
        if (color[0] === '#') {
            color = color.slice(1);
            usePound = true;
        }
        const num = parseInt(color, 16);
        let r = (num >> 16) + amt;
        if (r > 255)
            r = 255;
        else if (r < 0)
            r = 0;
        let b = ((num >> 8) & 0x00ff) + amt;
        if (b > 255)
            b = 255;
        else if (b < 0)
            b = 0;
        let g = (num & 0x0000ff) + amt;
        if (g > 255)
            g = 255;
        else if (g < 0)
            g = 0;
        return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
    };
    const getColor = function (color) {
        switch (color) {
            case 'main':
                return colors.mainColor;
            case 'mainLight':
                return colors.mainLight;
            case 'secondary':
                return colors.secondaryColor;
            case 'secondaryLight':
                return colors.secondaryLight;
            case 'error':
                return colors.errorColor;
            case 'done':
                return colors.doneColor;
            case 'dark':
                return colors.darkColor;
            case 'grey':
                return colors.greyColor;
            case 'placeholder':
                return colors.placeholderColor;
            default:
                return color;
        }
    };
    const getColorLight = function (color) {
        switch (color) {
            case 'main':
                return colors.mainLight;
            case 'secondary':
                return colors.secondaryLight;
            case 'error':
                return lightenDarkenColor(colors.errorColor, 20);
            case 'done':
                return lightenDarkenColor(colors.doneColor, 20);
            case 'dark':
                return lightenDarkenColor(colors.darkColor, 20);
            case 'grey':
                return lightenDarkenColor(colors.greyColor, 20);
            case 'placeholder':
                return lightenDarkenColor(colors.placeholderColor, 20);
            default:
                return lightenDarkenColor(color, 20);
        }
    };
    const getColorDark = function (color) {
        switch (color) {
            case 'main':
                return colors.mainDark;
            case 'secondary':
                return colors.secondaryDark;
            case 'error':
                return lightenDarkenColor(colors.errorColor, -20);
            case 'done':
                return lightenDarkenColor(colors.doneColor, -20);
            case 'dark':
                return lightenDarkenColor(colors.darkColor, -20);
            case 'grey':
                return lightenDarkenColor(colors.greyColor, -20);
            case 'placeholder':
                return lightenDarkenColor(colors.placeholderColor, -20);
            default:
                return lightenDarkenColor(color, -20);
        }
    };
    const getSize = function (size, px = false) {
        switch (size) {
            case 'article':
                return fonts.article;
            case 'subArticle':
                return fonts.subArticle;
            case 'title':
                return fonts.title;
            case 'titleMax':
                return fonts.titleMax;
            case 'subTitle':
                return fonts.subTitle;
            case 'button':
                return fonts.buttonTextSize;
            case 'note':
                return fonts.note;
            default:
                if (!px) {
                    if (size.includes('rem')) {
                        return size;
                    }
                    else {
                        console.error('CRONO ERROR: Font size must be in rem and not ' + size);
                        return fonts.article;
                    }
                }
                else
                    return size;
        }
    };
    const getControlStyle = function (styles, isFocused, color, textSize, theme) {
        return Object.assign(Object.assign({}, styles), { fontSize: getSize(textSize), width: 'inherit', height: 'auto', borderRadius: theme.borders.inputsBorderRadius, backgroundColor: 'white', cursor: 'pointer', border: isFocused ? `1px solid ${getColorLight(color)}` : borderColor(getColor('grey')), boxShadow: 0, '&:hover': {
                border: isFocused ? `1px solid ${getColorLight(color)}` : borderColor(getColor('grey'))
            } });
    };
    const getOptionStyle = function (styles, isDisabled, isFocused, isSelected, color, textSize, theme) {
        return Object.assign(Object.assign({}, styles), { fontFamily: fonts.fontFamily, fontSize: getSize(textSize), backgroundColor: isDisabled
                ? null
                : isSelected
                    ? getColor(color)
                    : isFocused
                        ? getColorLight(color)
                        : null, color: isDisabled
                ? theme.colors.placeholderColor
                : isSelected
                    ? 'white'
                    : isFocused
                        ? 'white'
                        : getColor(color), cursor: isDisabled ? 'not-allowed' : 'pointer' });
    };
    const getPlaceholderStyle = function (styles, textSize, theme) {
        return Object.assign(Object.assign({}, styles), { opacity: '0.6', fontSize: getSize(textSize), color: theme.colors.placeholderColor });
    };
    const getSingleValue = function (styles, color, textSize) {
        return Object.assign(Object.assign({}, styles), { fontSize: getSize(textSize), color: getColor(color) });
    };
    const getMultiValue = (styles, color, isDisabled, theme) => {
        return Object.assign(Object.assign({}, styles), { borderRadius: theme.borders.inputsBorderRadius, padding: 0, backgroundColor: getColorLight(isDisabled ? 'dark' : color) });
    };
    const getMultiValueLabel = (styles, textSize, theme) => {
        return Object.assign(Object.assign({}, styles), { fontSize: getSize(textSize), padding: theme.paddingsMargins.themeMenuPadding, marginLeft: theme.paddingsMargins.themeMenuMarginSingle, color: 'white' });
    };
    const getMultiValueRemove = (styles, color, theme) => {
        return Object.assign(Object.assign({}, styles), { color: 'white', ':hover': {
                backgroundColor: getColorDark(color),
                borderTopRightRadius: theme.borders.inputsBorderRadius,
                borderBottomRightRadius: theme.borders.inputsBorderRadius,
                color: 'white'
            } });
    };
    const getMenuPortal = (styles) => {
        return Object.assign(Object.assign({}, styles), { zIndex: 99999 });
    };
    return {
        lightenDarkenColor,
        getColor,
        getColorDark,
        getColorLight,
        getSize,
        getControlStyle,
        getOptionStyle,
        getPlaceholderStyle,
        getSingleValue,
        getMultiValue,
        getMultiValueLabel,
        customBorders,
        borderColor,
        borderColorDouble,
        getMultiValueRemove,
        getMenuPortal
    };
};
export default useThemeUtils;
